import type { InferType } from 'groqd'
import { q } from 'groqd'
import _max from 'lodash/max'
import _uniq from 'lodash/uniq'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { memo, useEffect, useMemo, useState } from 'react'

import Button from '@nxt-insurance/ui-kit/components/Button'
import Spinner from '@nxt-insurance/ui-kit/components/Spinner'
import Section from '@nxt-insurance/ui-kit/components/layoutComponents/Section'
import Spacer from '@nxt-insurance/ui-kit/components/layoutComponents/Spacer'
import { body1, h2, h4 } from '@nxt-insurance/ui-kit/core/typography'

import { runQuery } from '@/cms/client'
import { BUTTONS_IDS, EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { getLocaleParts } from '@/utils/locale'
import { removeImpermanentSearchParams } from '@/utils/url'
import { useQuery } from '@tanstack/react-query'

const INITIAL_ARTICLES_SHOWN = 3

const pressReleaseQuery = q('*')
  .filter(
    '_type == "pressRelease" && $market in availableMarkets && $lang in availableLanguages && publishDate < now() && enabledInProduction != false'
  )
  .order('publishDate desc')
  .grab({
    _id: q.string(),
    description: ['description[$lang]', q.string()],
    heading: ['heading[$lang]', q.string()],
    pdfHref: q('pdf[$lang]').grab({
      asset: q('asset').deref().grabOne('url', q.string()),
    }),
    publishDate: q.string(),
    publication: q('publication').deref().grabOne('slug[$lang].current', q.string().nullable()),
  })

const PressRelease = (props: { pressRelease: InferType<typeof pressReleaseQuery>[number] }) => {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const { query } = useRouter()
  const pressReleaseUrl = useMemo(() => {
    if (!props.pressRelease.publication) {
      return
    }
    const whitelistedSearchParams = removeImpermanentSearchParams(query)
    if (whitelistedSearchParams.keys.length === 0) {
      return `/posts/${props.pressRelease.publication}`
    }
    return `/posts/${props.pressRelease.publication}?${whitelistedSearchParams.toString()}`
  }, [props.pressRelease.publication, query])
  return (
    <div className="mb-8 flex flex-col gap-y-4 border-b-[3px] border-solid border-ui-grey-50 pb-8">
      <span>{props.pressRelease.publishDate}</span>
      <Link className={h4()} href={pressReleaseUrl || props.pressRelease.pdfHref.asset}>
        {props.pressRelease.heading}
      </Link>
      <p className={body1()}>{props.pressRelease.description}</p>
      <div className="flex flex-row pb-8">
        {pressReleaseUrl ? (
          <Link className="pr-4" href={pressReleaseUrl} target="_blank">
            <Button
              onPress={() => {
                track(EVENTS.ButtonClicked, {
                  buttonId: BUTTONS_IDS.PRESS_RELEASE_LINK,
                  type: 'link',
                  href: pressReleaseUrl,
                  pressReleaseId: props.pressRelease._id,
                })
              }}
              type="outlined"
              variantColor="brandDark"
              uppercase
              label={t('press.buttons.showPublication')}
            />
          </Link>
        ) : null}
        <Link href={props.pressRelease.pdfHref.asset} target="_blank">
          <Button
            onPress={() => {
              track(EVENTS.ButtonClicked, {
                buttonId: BUTTONS_IDS.PRESS_RELEASE_PDF,
                type: 'link',
                href: pressReleaseUrl,
                pressReleaseId: props.pressRelease._id,
              })
            }}
            variantColor="brandDark"
            uppercase
            type="outlined"
            label={t('press.buttons.showPdf')}
          />
        </Link>
      </div>
    </div>
  )
}

export const PressArchive = memo(() => {
  const { locale } = useRouter()
  const [lang, market] = getLocaleParts(locale)
  const { t } = useTranslation()
  const { data: pressReleases, isLoading } = useQuery({
    queryKey: ['pressRelease', lang, market],
    queryFn: async () =>
      runQuery(pressReleaseQuery, {
        lang,
        market,
      }),
    throwOnError: true,
  })
  const { track } = useAnalytics()
  const availableYears = useMemo(
    () => _uniq(pressReleases?.map((el) => el.publishDate.slice(0, 4))),
    [pressReleases]
  )

  const currentYear = useMemo(() => _max(availableYears), [availableYears])

  const [expanded, setExpanded] = useState(false)
  const [activeYear, setActiveYear] = useState<string | undefined>()
  useEffect(() => {
    if (currentYear && !activeYear) {
      setActiveYear(currentYear)
    }
  }, [currentYear, activeYear])

  const pressReleasesFilteredByYear = useMemo(() => {
    if (!pressReleases) {
      return []
    }
    return pressReleases.filter((PR) => PR.publishDate.slice(0, 4) === activeYear || currentYear)
  }, [activeYear, currentYear, pressReleases])

  if (isLoading) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-4">
        <Spinner />
      </div>
    )
  }
  if (!pressReleases) {
    return null
  }
  return (
    <div className="flex w-full flex-col items-center justify-center">
      <div className="flex w-full max-w-screen-2xl flex-col p-8">
        <Section>
          <p className={h2()}>{t('press.header')}</p>
          <Spacer size="small" />
          <div className="flex flex-row pb-8">
            {availableYears.map((year) => (
              <div className="pr-4" key={year}>
                <Button
                  type="outlined"
                  variantColor="brandDark"
                  uppercase
                  onPress={() => {
                    track(EVENTS.ButtonClicked, {
                      buttonId: BUTTONS_IDS.PRESS_PAGE_YEAR,
                      type: 'button',
                      year,
                    })

                    setActiveYear(year)
                  }}
                  disabled={activeYear === year}
                  label={year}
                />
              </div>
            ))}
          </div>
          {pressReleasesFilteredByYear.map((pressRelease, index) =>
            index < INITIAL_ARTICLES_SHOWN || expanded ? (
              <PressRelease key={pressRelease._id} pressRelease={pressRelease} />
            ) : null
          )}
          {pressReleasesFilteredByYear.length > INITIAL_ARTICLES_SHOWN ? (
            <div className="flex flex-col items-center">
              <Button
                onPress={() => {
                  track(EVENTS.ButtonClicked, {
                    buttonId: expanded
                      ? BUTTONS_IDS.LOAD_LESS_PRESS_RELEASES
                      : BUTTONS_IDS.LOAD_MORE_PRESS_RELEASES,
                    type: 'button',
                  })
                  setExpanded((currentExpanded) => !currentExpanded)
                }}
                variantColor="brandDark"
                uppercase
                label={t(expanded ? 'press.buttons.showLess' : 'press.buttons.showMore')}
              />
            </div>
          ) : null}
        </Section>
      </div>
    </div>
  )
})
